import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SDialog } from '@modules/ui-components';
import type { BillingCreditLedgerEntry } from '../types';

type OperationFilter = 'all' | 'credit' | 'debit';

interface CreditLedgerHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  ledger: BillingCreditLedgerEntry[];
  fallbackCurrency?: string;
  loading?: boolean;
  onRefresh?: () => void | Promise<void>;
  onOpenBackendTask?: (taskId: string, entry: BillingCreditLedgerEntry) => void;
}

function moneyToLabel(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

function formatDate(value?: string): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString();
}

function dateInputToStart(value: string): number | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

function dateInputToEnd(value: string): number | null {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

function operationLabel(entry: BillingCreditLedgerEntry): string {
  if (entry.deltaMinor > 0) return 'Top-up';
  if (entry.deltaMinor < 0) return 'Spend';
  return 'Neutral';
}

function operationColor(entry: BillingCreditLedgerEntry): 'success' | 'warning' | 'default' {
  if (entry.deltaMinor > 0) return 'success';
  if (entry.deltaMinor < 0) return 'warning';
  return 'default';
}

function metadataString(entry: BillingCreditLedgerEntry, key: string): string | undefined {
  const value = entry.metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function extractTaskId(value: string | undefined): string | null {
  if (!value) return null;

  const autoIdMatch = value.match(/(?:^|_)task_([A-Za-z0-9]{20})(?:_|$)/);
  if (autoIdMatch?.[1]) return autoIdMatch[1];

  const agentTaskAutoIdMatch = value.match(/(?:^|_)agent_task_([A-Za-z0-9]{20})(?:_|$)/);
  if (agentTaskAutoIdMatch?.[1]) return agentTaskAutoIdMatch[1];

  const directTaskMatch = value.match(/^task_([^_]+)_/);
  if (directTaskMatch?.[1]) return directTaskMatch[1];

  const agentTaskMatch = value.match(/(?:^|_)agent_task_([^_]+)_/);
  return agentTaskMatch?.[1] || null;
}

export function getCreditLedgerBackendTaskId(entry: BillingCreditLedgerEntry): string | null {
  if (entry.source !== 'ai_usage') return null;

  const explicitTaskId = metadataString(entry, 'taskId') || metadataString(entry, 'backendTaskId');
  if (explicitTaskId) return explicitTaskId;

  const candidates = [
    metadataString(entry, 'reservationId'),
    metadataString(entry, 'usageEventId'),
    entry.sourceId,
    entry.id,
  ];

  for (const candidate of candidates) {
    const taskId = extractTaskId(candidate);
    if (taskId) return taskId;
  }

  return null;
}

export function CreditLedgerHistoryDialog({
  open,
  onClose,
  ledger,
  fallbackCurrency = 'usd',
  loading = false,
  onRefresh,
  onOpenBackendTask,
}: CreditLedgerHistoryDialogProps): React.ReactElement {
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [operation, setOperation] = React.useState<OperationFilter>('all');

  React.useEffect(() => {
    if (!open) return;
    setFromDate('');
    setToDate('');
    setOperation('all');
  }, [open]);

  const filteredLedger = React.useMemo(() => {
    const fromTime = dateInputToStart(fromDate);
    const toTime = dateInputToEnd(toDate);
    return ledger.filter((entry) => {
      if (operation === 'credit' && entry.deltaMinor <= 0) return false;
      if (operation === 'debit' && entry.deltaMinor >= 0) return false;
      const entryTime = new Date(entry.createdAt).getTime();
      if (!Number.isFinite(entryTime)) return true;
      if (fromTime !== null && entryTime < fromTime) return false;
      if (toTime !== null && entryTime > toTime) return false;
      return true;
    });
  }, [fromDate, ledger, operation, toDate]);

  const netMinor = React.useMemo(
    () => filteredLedger.reduce((sum, entry) => sum + entry.deltaMinor, 0),
    [filteredLedger],
  );
  const currency = filteredLedger[0]?.currency || ledger[0]?.currency || fallbackCurrency;
  const hasFilters = Boolean(fromDate || toDate || operation !== 'all');

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Credit ledger history"
      maxWidth="md"
      fullWidth
      showCloseButton={false}
      secondaryAction={{
        label: 'Close',
        onClick: onClose,
      }}
    >
      <Stack spacing={1.5} sx={{ px: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="From date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="credit-ledger-operation-filter-label">Operation</InputLabel>
            <Select
              labelId="credit-ledger-operation-filter-label"
              label="Operation"
              value={operation}
              onChange={(event) => setOperation(event.target.value as OperationFilter)}
            >
              <MenuItem value="all">All operations</MenuItem>
              <MenuItem value="credit">Top-ups only</MenuItem>
              <MenuItem value="debit">Spends only</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            disabled={!hasFilters}
            onClick={() => {
              setFromDate('');
              setToDate('');
              setOperation('all');
            }}
          >
            Clear
          </Button>
          {onRefresh ? (
            <Button startIcon={<RefreshIcon />} disabled={loading} onClick={onRefresh}>
              Refresh
            </Button>
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={`${filteredLedger.length} entries`} />
          <Chip
            size="small"
            color={netMinor < 0 ? 'warning' : netMinor > 0 ? 'success' : 'default'}
            label={`Net ${netMinor < 0 ? '-' : '+'}${moneyToLabel(Math.abs(netMinor), currency)}`}
          />
        </Stack>

        <Divider />

        {filteredLedger.length === 0 ? (
          <Alert severity="info">No credit ledger entries match the selected filters.</Alert>
        ) : (
          <Stack spacing={1}>
            {filteredLedger.map((entry) => {
              const taskId = getCreditLedgerBackendTaskId(entry);
              const canOpenTask = Boolean(taskId && onOpenBackendTask);
              const openTask = () => {
                if (taskId) onOpenBackendTask?.(taskId, entry);
              };

              return (
                <Paper
                  key={entry.id}
                  variant="outlined"
                  role={canOpenTask ? 'button' : undefined}
                  tabIndex={canOpenTask ? 0 : undefined}
                  onClick={canOpenTask ? openTask : undefined}
                  onKeyDown={(event) => {
                    if (!canOpenTask) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openTask();
                    }
                  }}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    cursor: canOpenTask ? 'pointer' : 'default',
                    transition: 'border-color 120ms ease, background-color 120ms ease',
                    '&:hover': canOpenTask
                      ? {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        }
                      : undefined,
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                          {entry.source}
                        </Typography>
                        <Chip size="small" label={operationLabel(entry)} color={operationColor(entry)} />
                        <Chip size="small" label={entry.type} variant="outlined" />
                        {canOpenTask ? (
                          <Chip size="small" label="Backend task" variant="outlined" color="primary" />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                        {formatDate(entry.createdAt)}
                        {entry.sourceId ? ` · ${entry.sourceId}` : ''}
                        {entry.createdBy ? ` · by ${entry.createdBy}` : ''}
                      </Typography>
                    </Box>
                    {canOpenTask ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        color="primary.main"
                        sx={{ minWidth: { md: 104 } }}
                      >
                        <OpenInNewIcon fontSize="small" />
                        <Typography variant="body2">Open task</Typography>
                      </Stack>
                    ) : null}
                    <Stack spacing={0.25} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                      <Typography color={entry.deltaMinor < 0 ? 'error.main' : 'success.main'}>
                        {entry.deltaMinor < 0 ? '-' : '+'}
                        {moneyToLabel(Math.abs(entry.deltaMinor), entry.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Balance {moneyToLabel(entry.balanceAfterMinor, entry.currency)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Stack>
    </SDialog>
  );
}

export default CreditLedgerHistoryDialog;
